import css from './styles/top-menu.scss';
import React from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useSelector } from 'react-redux';
import { RootState } from 'bootstrap';
import { getUser } from '../../redux/actions/appActions';
import LocalAuth from '@shamrock-core/common/authentication/local-authentication';
import Permission from '../core/permission';
import Feature from '../core/feature';
import FirebaseAnalytics from '../core/analytics';
import { getDeviceType, redirectToLogout, redirectToV2Logout } from '../../utils/utils';
import { useNavigate } from 'react-router-dom';
import airshipService from '../../services/airship.service';
import { getAirshipChannelId } from '../../utils/airshipHandler';
import { useTranslation } from 'react-i18next';
import arrowDropdown from '../../assets/images/arrow-dropdown.svg';
import { useAuth0 } from '@auth0/auth0-react';

const Config = require('Config');

const TopMenu = () => {
  const navigate = useNavigate();
  const user = useSelector(getUser);
  const cpua3558 = useSelector((state: RootState) => state.app.features.includes('CPUA-3558-WEB'));
  const cpua5206 = useSelector((state: RootState) => state.app.features.includes('CPUA-5206'));
  const useNewApiKeyRoute = useSelector((state: RootState) =>
    state.app.features.includes('f1a582e022b8ef05ca314a28e7699d234d960443adf23921b6d7ba9285072290'),
  );
  const { isAuthenticated: isAuthenticatedAuth0 } = useAuth0();
  const [anchorEl, setAnchorEl] = React.useState(undefined);
  const firstName = user?.firstname || '';
  const lastName = user?.lastname ? ` ${user?.lastname}` : '';
  const token = LocalAuth.getTokenValue();
  const email = LocalAuth.getUser().email as string;
  const authQuery = `&token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}`;
  const defaultAuthQuery = `?token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}`;
  const { t } = useTranslation();

  const handleClick = (label: string) => {
    const { width, height } = window.screen;
    setAnchorEl(null);
    const params = {
      [FirebaseAnalytics.PARAMETERS.BROWSER_TYPE]: getDeviceType(),
      [FirebaseAnalytics.PARAMETERS.DESCRIPTION]: label,
      [FirebaseAnalytics.PARAMETERS.RESOLUTION]: `${width}X${height}`,
      [FirebaseAnalytics.PARAMETERS.DATETIMESTAMP]: Date.now().toString(),
    };
    FirebaseAnalytics.sendEvent(
      FirebaseAnalytics.EVENTS.CLICK,
      FirebaseAnalytics.MODULES.RTSPRO,
      FirebaseAnalytics.PAGES.TOP_MENU,
      params,
    );
  };

  const handleSettingsClicked = () => {
    handleClick(FirebaseAnalytics.LABELS.SETTINGS);
    navigate('/settings');
  };

  const handleAdminClicked = () => {
    handleClick(FirebaseAnalytics.LABELS.ADMINISTRATION);
    const url = `${Config.adminUserMangementUrl}${defaultAuthQuery}`;
    window.open(url, '_blank');
  };

  const handleApiKeysClicked = () => {
    handleClick(FirebaseAnalytics.LABELS.API_KEYS);
    useNewApiKeyRoute
      ? window.open(`${Config.apiKeysUrl}${defaultAuthQuery}`, '_blank')
      : window.open(`${Config.oldApiKeysUrl}${defaultAuthQuery}`, '_blank');
  };

  const handleWidgetCMSClicked = () => {
    window.open(`${Config.widgetCMSUrl}${defaultAuthQuery}`, '_blank');
  };

  const handleQuotingAdminClicked = () => {
    handleClick(FirebaseAnalytics.LABELS.QUOTING_ADMIN);
    window.open(`${Config.quotingAdminUrl}${authQuery}`, '_blank');
  };

  const handleContactClicked = () => {
    handleClick(FirebaseAnalytics.LABELS.CONTACT_US);
    const url = cpua3558 ? `${Config.adminContactUsUrl}${defaultAuthQuery}` : Config.contactUsUrl;
    window.open(url, '_blank');
  };

  const handleCarrierDirectoryClicked = () => {
    window.open(`${Config.carrierDirectoryUrl}${defaultAuthQuery}`, '_blank');
  };

  const handleLogoutClicked = async () => {
    handleClick(FirebaseAnalytics.LABELS.LOGOUT);
    if (!cpua5206) {
      try {
        const airshipChannelId = await getAirshipChannelId();
        await airshipService.deleteDeviceInformation(airshipChannelId, email);
      } catch (err) {
        console.log('A problem occured while removing a device information.', err);
      }
    }
    isAuthenticatedAuth0 ? redirectToV2Logout() : redirectToLogout();
  };

  return (
    <div className={css.appBar}>
      <div className={css.toolBar}>
        <div className={css.rightSideHeader}>
          <div
            data-testid="log-out-dropdown"
            className={css.menuItem}
            onClick={(event) => setAnchorEl(event.currentTarget)}
          >
            {firstName + lastName}
            <div className={anchorEl ? css.open : css.closed}>
              <img src={arrowDropdown} />
            </div>
          </div>
        </div>
        <Menu
          classes={{ paper: css.menuRoot }}
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          <Permission
            permission={[
              'carrierpro.PermissionsAdmin',
              'carrierpro.ViewRole',
              'carrierpro.CompanyAdmin',
              'carrierpro.RepCompanyAdmin',
            ]}
          >
            <MenuItem style={{ width: '200px' }} onClick={handleAdminClicked}>
              <div className={css.menuItem}>{t('administration')}</div>
            </MenuItem>
          </Permission>
          <Permission permission="api.User">
            <MenuItem style={{ width: '200px' }} onClick={handleApiKeysClicked}>
              <div className={css.menuItem}>API Keys</div>
            </MenuItem>
          </Permission>
          <Feature feature="CPUA-3642">
            <Permission permission="carrierpro.RepCompanyAdmin">
              <MenuItem style={{ width: '200px' }} onClick={handleCarrierDirectoryClicked}>
                <div className={css.menuItem}>Carrier Directory</div>
              </MenuItem>
            </Permission>
          </Feature>
          <MenuItem style={{ width: '200px' }} onClick={handleContactClicked}>
            <div className={css.menuItem}>{t('contact_us')}</div>
          </MenuItem>
          <Permission permission="ryantrans.LoadboardInternalAdmin">
            <MenuItem style={{ width: '200px' }} onClick={handleQuotingAdminClicked}>
              <div className={css.menuItem}>{t('quoting_admin')}</div>
            </MenuItem>
          </Permission>
          <MenuItem style={{ width: '200px' }} onClick={handleSettingsClicked}>
            <div className={css.menuItem}>{t('settings')}</div>
          </MenuItem>
          <Permission permission="carrierpro.DashboardWidgets">
            <MenuItem style={{ width: '200px' }} onClick={handleWidgetCMSClicked}>
              <div className={css.menuItem}>{t('widget_cms')}</div>
            </MenuItem>
          </Permission>
          <MenuItem style={{ width: '200px' }} onClick={handleLogoutClicked}>
            <div className={css.menuItem} data-testid="log-out-button">
              {t('log_out')}
            </div>
          </MenuItem>
        </Menu>
      </div>
    </div>
  );
};

export default TopMenu;
